import React, { ReactNode } from 'react';
import {
  Content,
  CreateButton,
  Header,
  Link,
  Page,
} from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogFilterLayout,
  EntityListProvider,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { Box } from '@material-ui/core';
import { EntityRefLinkWithVuid, PageNotice } from '@internal/plugin-rtl-data';

/**
 * The stock columns of the listing, with the vuid of a team or a product as a
 * chip next to its name, which copies it - the name the entity presentation
 * gives no longer carries it, see `renderEntityPresentation`. Sorting, the
 * highlight and the search stay those of the stock column: it searches the
 * entity ref, which holds the vuid.
 */
const withVuidInNames: CatalogTableColumnsFunc = context =>
  CatalogTable.defaultColumnsFunc(context).map(column =>
    column.field === 'resolved.entityRef'
      ? {
          ...column,
          render: (row: CatalogTableRow) => (
            <EntityRefLinkWithVuid
              entityRef={row.entity}
              defaultKind={context.filters.kind?.value}
            />
          ),
        }
      : column,
  );

/**
 * The catalog listing: the stock one, put together from the same parts, with
 * the title the sidebar gives it and a note on where its content comes from.
 *
 * The stock page offers no room above the list and names itself after the
 * organisation, which is why it is not used as it is. `CatalogIndexPage`
 * renders this in its place when it is mounted as the child of its route.
 *
 * The Create button sits in the header rather than in a row of its own above
 * the list, so that the note is the first thing in the content as on every
 * other page that has one. The support button is left out: with no support
 * configured for the app it only lists Backstage's own links.
 */
export const CatalogPage = (props: { filters: ReactNode }) => {
  const { allowed: canCreate } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header title="Catalog" pageTitleOverride="Catalog">
          {canCreate && <CreateButton title="Create" to="/create" />}
        </Header>
        <Content>
          <PageNotice
            showLabel="Show sources"
            hideLabel="Hide sources"
            details={
              <Box component="ul" mt={0.5} mb={0} pl={2.5}>
                <li>
                  <Link to="https://gitlab.com/dataalliance/infra/common/cmdb">
                    Developer Platform database
                  </Link>{' '}
                  - main source
                </li>
                <li>
                  <Link to="https://service-catalog.netrtl.com/">
                    Service Catalog
                  </Link>
                </li>
                <li>
                  <Link to="https://serviceportal.netrtl.com/">ServiceNow</Link>
                </li>
                <li>Microsoft Entra</li>
                <li>Google Cloud</li>
                <li>catalog-info.yaml files in GitLab repositories</li>
              </Box>
            }
          >
            The catalog is not edited here: it is built from the source systems
            and refreshed regularly, so a change made there shows up here with
            the next refresh.
          </PageNotice>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              {props.filters}
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTable columns={withVuidInNames} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
};
